import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useStore } from '../../../stores/store';
import { ServicesSubSectionHeader } from '../../services/services-subsection/services-subsection.styles';
import {
  BasicInsuranceTitle,
  CustomHeader,
  FullInsuranceTitle,
  FullInsuranceValue,
  InfoText,
  InsuranceFeeCoverageContainer,
  InsuranceRadioDescription,
  TitleAndTag,
  TitleText,
} from './insurance-fee.styles';
import InsuranceTooltip from '../../../common/insurance-tooltip/insurance-tooltip.component';
import RadioGroup from '../../../common/radio-group/radio-group.component';
import Radio from '../../../common/radio/radio.component';
import { InsuranceCoverage } from '../../../models/parcelDeliveryMiles';
import { getFormattedPrice } from '../../../utils/price/get-formatted-price';
import Tag from '../../../common/tag/tag.component';
import { useModal } from '../../../common/modal';
import { Modals } from '../../../constants/modals';
import ParcelNonCompensatedItemsWithSidebar from '../parcel-non-compensated-items-sidebar/parcel-non-compensated-items-sidebar.component';
import { InsuranceLimits } from '../../../models/insuranceLimits';
import { I18TranslateFunc } from '../../../../i18n';
import ManualInsuredValueInput from './manual-insured-value-input.component';
import CustomSkeleton from '../../../common/skeletons/custom-skeleton-component';
import { getCountryTranslationKey } from '../../../utils/translations/translations.utils';

function getMaxInsuranceStrings(
  insuranceLimits: InsuranceLimits | undefined,
  currencySymbol: string,
  isPartial: boolean,
  departureCountryCode: string | undefined,
  t: I18TranslateFunc
) {
  const maxInsuredValue = getFormattedPrice(
    insuranceLimits?.max_insurance,
    currencySymbol
  );

  const title = isPartial
    ? t('partial_insurance_title', {
        InsuranceLimit: maxInsuredValue,
      })
    : t(getCountryTranslationKey('full_insurance_title', departureCountryCode));

  const description = isPartial
    ? t('partial_insurance_description', {
        InsuranceLimit: maxInsuredValue,
        InsuranceFeePercent: (insuranceLimits?.coefficient ?? 0.05) * 100,
      })
    : t(
        getCountryTranslationKey(
          'full_insurance_description',
          departureCountryCode
        ),
        {
          InsuranceFeePercent: (insuranceLimits?.coefficient ?? 0.05) * 100,
        }
      );

  return { title, description };
}

const InsuranceFeeSimplified = () => {
  const { t } = useTranslation();
  const {
    parcelCreationStore: {
      shipment,
      fetchDomesticInsuranceLimits,
      insuranceLimits,
      updateShipmentInsuranceCoverage,
      navigation,
      isLoadingShipmentApi,
      countryDeparture,
    },
    shipmentCountriesStore: { settings, getCountrySetting },
    userStore: { getCurrencySymbol },
  } = useStore();

  const { openModal, closeModal } = useModal();

  const isManualInsuredValueAllowed = useMemo(
    () =>
      getCountrySetting(countryDeparture?.code, 'insurance')?.value ===
      'from_items_to_top',
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [countryDeparture?.code, settings, getCountrySetting]
  );
  const [isFullCoverageDisabled, setisFullCoverageDisabled] = useState(false);
  const currencySymbol = getCurrencySymbol(insuranceLimits?.currency);
  const totalDeclaredValue = shipment?.total_declared_value ?? 0;
  const maxInsurance = insuranceLimits?.max_insurance ?? 0;

  useEffect(() => {
    fetchDomesticInsuranceLimits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shipmentCoverage = useMemo(
    () =>
      shipment && shipment.insurance_mode !== ''
        ? shipment.insurance_mode
        : 'minimal_coverage',
    [shipment]
  );

  const [currentCoverage, setCurrentCoverage] =
    useState<InsuranceCoverage>(shipmentCoverage);

  // // sync backend coverage to the UI
  useEffect(() => {
    if (shipmentCoverage !== currentCoverage) {
      setCurrentCoverage(shipmentCoverage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCoverage, shipmentCoverage, updateShipmentInsuranceCoverage]);

  useEffect(() => {
    if (!shipment?.total_declared_value || !insuranceLimits?.max_free_value) {
      setisFullCoverageDisabled(true);
      return;
    }

    if (isManualInsuredValueAllowed) {
      setisFullCoverageDisabled(false);
      return;
    }

    // No sense to allow "full_coverage" in case total declared value is less then free insured value.
    if (insuranceLimits?.max_free_value >= shipment.total_declared_value) {
      setisFullCoverageDisabled(true);
    } else {
      setisFullCoverageDisabled(false);
    }
  }, [
    shipment?.total_declared_value,
    insuranceLimits?.max_free_value,
    isManualInsuredValueAllowed,
  ]);

  const handleCoverageChange = (newCoverage: InsuranceCoverage) => {
    if (newCoverage === 'minimal_coverage') {
      openModal({
        id: Modals.INSURANCE_COVERAGE_CHANGE,
        name: Modals.INSURANCE_COVERAGE_CHANGE,
        props: {
          onApprove: () => {
            setCurrentCoverage(newCoverage);
            updateShipmentInsuranceCoverage(newCoverage);
            closeModal(Modals.INSURANCE_COVERAGE_CHANGE);
          },
          onClose: () => {
            closeModal(Modals.INSURANCE_COVERAGE_CHANGE);
          },
        },
      });
      return;
    }
    setCurrentCoverage(newCoverage);
    updateShipmentInsuranceCoverage(newCoverage);
  };

  // Only use 'partial insurance' mode in case total declared value exceeds the limits
  const isPartialInsurance = totalDeclaredValue > maxInsurance;

  const insuranceFee = shipment?.insurance_fee ?? 0;

  const insuranceFeeFormatted = getFormattedPrice(insuranceFee, currencySymbol);

  const maxInsuranceString = getMaxInsuranceStrings(
    insuranceLimits,
    currencySymbol,
    isPartialInsurance,
    countryDeparture?.code,
    t
  );

  const showNonCompensatedItems = useMemo(() => {
    const insuranceElement = navigation.current?.elements.find(
      (el) => el.name === 'insurance'
    );

    return !!insuranceElement?.elements?.find(
      (el) => el.name === 'show-non-compensated-items'
    );
  }, [navigation]);

  const maxInsuranceTitle = (
    <FullInsuranceTitle>
      <TitleAndTag>
        <TitleText>{maxInsuranceString.title}</TitleText>
        <Tag
          size='small'
          type='semi-filled'
          color='sky-blue'>
          {t('delivery_options_sorting_recommended')}
        </Tag>
      </TitleAndTag>
      {insuranceFee > 0 &&
        !isFullCoverageDisabled &&
        (isLoadingShipmentApi ? (
          <CustomSkeleton
            width='6rem'
            height='2.2rem'
            radius={8}
          />
        ) : (
          <FullInsuranceValue>{insuranceFeeFormatted}</FullInsuranceValue>
        ))}
    </FullInsuranceTitle>
  );

  const maxInsuranceDescription = (
    <InsuranceRadioDescription>
      {maxInsuranceString.description}
      {isManualInsuredValueAllowed && (
        <ManualInsuredValueInput
          currentCoverage={currentCoverage}
          isFullCoverageDisabled={isFullCoverageDisabled}
          currencySymbol={currencySymbol}
        />
      )}
    </InsuranceRadioDescription>
  );

  const basicInsuranceTitle = (
    <BasicInsuranceTitle>
      <TitleText>
        {t(
          getCountryTranslationKey(
            'basic_insurance_title',
            countryDeparture?.code
          )
        )}
      </TitleText>
      <InfoText>{t('free_subtitle')}</InfoText>
    </BasicInsuranceTitle>
  );

  return (
    <InsuranceFeeCoverageContainer>
      <ServicesSubSectionHeader>
        <CustomHeader>
          {t(getCountryTranslationKey('insurance', countryDeparture?.code))}
          <InsuranceTooltip />
        </CustomHeader>
      </ServicesSubSectionHeader>
      <RadioGroup direction='column'>
        <Radio
          name='measures'
          value='1'
          disabled={isFullCoverageDisabled}
          checked={currentCoverage === 'full_coverage'}
          title={maxInsuranceTitle}
          description={maxInsuranceDescription}
          onChange={() => handleCoverageChange('full_coverage')}
        />
        <Radio
          name='measures'
          value='2'
          checked={currentCoverage === 'minimal_coverage'}
          title={basicInsuranceTitle}
          description={t(
            getCountryTranslationKey(
              'basic_insurance_description',
              countryDeparture?.code
            ),
            {
              FreeInsuranceLimit: getFormattedPrice(
                insuranceLimits?.max_free_value,
                currencySymbol
              ),
            }
          )}
          onChange={() => handleCoverageChange('minimal_coverage')}
        />
      </RadioGroup>
      {showNonCompensatedItems && <ParcelNonCompensatedItemsWithSidebar />}
    </InsuranceFeeCoverageContainer>
  );
};

export default observer(InsuranceFeeSimplified);
